import { useCallback, useEffect, useState } from "react";
import {
  fetchInformeSeoTecnicoHtml,
  fetchSeoTecnico,
  generarInformeSeoTecnico,
  runLighthousePlantillas,
  urlInformeSeoTecnico,
  type SeoTecnicoEstado,
  type SeoTecnicoPlantilla,
} from "../../api";

export interface FilaPlantilla extends SeoTecnicoPlantilla {
  marcada: boolean;
}

export type Ocupado = "cargando" | "lighthouse" | "generando" | "ajustando" | null;

/** "2026-09-28" o un ISO con hora → "28 sept 2026" (las fechas sin hora se leen en hora local). */
export function fechaCorta(valor: string | null | undefined, conHora = false): string {
  if (!valor) return "Sin datos";
  const d = new Date(valor.length === 10 ? `${valor}T12:00:00` : valor);
  if (Number.isNaN(d.getTime())) return valor;
  return d.toLocaleString("es-CL", {
    day: "numeric", month: "short", year: "numeric",
    ...(conHora ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

export const OCUPADO_TEXTO: Record<Exclude<Ocupado, null>, string> = {
  cargando: "Leyendo fuentes…",
  lighthouse: "Corriendo Lighthouse en las URLs marcadas (hasta 2 min)…",
  generando: "Gertrudis está redactando el informe (2 a 5 min)…",
  ajustando: "Gertrudis está aplicando el ajuste (2 a 5 min)…",
};

export function versionTexto(i: { version: number; created_at: string; instruccion: string | null }): string {
  const base = `Versión ${i.version} · ${fechaCorta(i.created_at, true)}`;
  return i.instruccion ? `${base} · ajuste: ${i.instruccion.slice(0, 60)}${i.instruccion.length > 60 ? "…" : ""}` : base;
}

/** Umbrales de color de Lighthouse: 90+ bueno, 50-89 por mejorar, bajo 50 malo. */
export function nivelPuntaje(valor: number | null | undefined): string {
  if (valor == null) return "is-empty";
  return valor >= 90 ? "is-good" : valor >= 50 ? "is-mid" : "is-bad";
}

function mensaje(causa: unknown): string {
  const texto = causa instanceof Error ? causa.message : String(causa);
  try {
    return JSON.parse(texto).detail ?? texto;
  } catch {
    return texto;
  }
}

/** Estado y acciones del informe de SEO técnico; lo comparten la vista de escritorio y la móvil. */
export function useSeoTecnico(clientSlug: string) {
  const [estado, setEstado] = useState<SeoTecnicoEstado | null>(null);
  const [filas, setFilas] = useState<FilaPlantilla[]>([]);
  const [version, setVersion] = useState<number | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<Ocupado>("cargando");
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const cargar = useCallback(async (versionElegida?: number) => {
    const e = await fetchSeoTecnico(clientSlug);
    setEstado(e);
    setFilas(e.plantillas.map((p) => ({ ...p, marcada: !p.lighthouse })));
    setVersion(versionElegida ?? e.informes[0]?.version ?? null);
  }, [clientSlug]);

  useEffect(() => {
    setOcupado("cargando");
    setError(null);
    setAviso(null);
    setHtml(null);
    cargar().catch((c) => setError(mensaje(c))).finally(() => setOcupado(null));
  }, [cargar]);

  useEffect(() => {
    if (version == null) return setHtml(null);
    let vigente = true;
    setHtml(null);
    fetchInformeSeoTecnicoHtml(clientSlug, version)
      .then((h) => vigente && setHtml(h))
      .catch((c) => vigente && setError(mensaje(c)));
    return () => { vigente = false; };
  }, [clientSlug, version]);

  const ejecutar = async (tipo: Exclude<Ocupado, null>, accion: () => Promise<void>): Promise<boolean> => {
    setOcupado(tipo);
    setError(null);
    setAviso(null);
    try {
      await accion();
      return true;
    } catch (c) {
      setError(mensaje(c));
      return false;
    } finally {
      setOcupado(null);
    }
  };

  const seleccion = filas.filter((f) => f.marcada && f.plantilla.trim() && f.url.trim());

  const correrLighthouse = () => ejecutar("lighthouse", async () => {
    const r = await runLighthousePlantillas(clientSlug, seleccion.map((f) => ({ plantilla: f.plantilla.trim(), url: f.url.trim() })));
    await cargar(version ?? undefined);
    setAviso(r.errores.length
      ? `Lighthouse listo en ${r.ok} URL. Sin resultado: ${r.errores.map((e) => e.url).join(", ")}`
      : `Lighthouse listo en ${r.ok} URL.`);
  });

  const generar = () => ejecutar("generando", async () => {
    const r = await generarInformeSeoTecnico(clientSlug);
    await cargar(r.version);
  });

  const ajustar = (instruccion: string) => ejecutar("ajustando", async () => {
    if (version == null) return;
    const r = await generarInformeSeoTecnico(clientSlug, { instruccion, baseVersion: version });
    await cargar(r.version);
  });

  const editarFila = (indice: number, cambio: Partial<FilaPlantilla>) =>
    setFilas((fs) => fs.map((f, i) => (i === indice ? { ...f, ...cambio } : f)));
  const agregarFila = () =>
    setFilas((fs) => [...fs, { plantilla: "", url: "", paginas: null, lighthouse: null, marcada: true }]);

  const f = estado?.fuentes;
  const fuentes = [
    { nombre: "Megaexport Semrush", fecha: fechaCorta(f?.megaexport.fecha), detalle: f ? `${f.megaexport.urls} URLs` : "" },
    { nombre: "Issues Semrush", fecha: fechaCorta(f?.issues.fecha), detalle: "Gravedad de cada problema" },
    { nombre: "Lighthouse", fecha: fechaCorta(f?.lighthouse.fecha), detalle: f ? `${f.lighthouse.urls} URLs` : "" },
    { nombre: "robots.txt y sitemap", fecha: "Al generar", detalle: "Se leen en vivo del sitio" },
  ];

  return {
    estado,
    fuentes,
    sinMegaexport: !!estado && !estado.fuentes.megaexport.fecha,
    filas,
    seleccion,
    editarFila,
    agregarFila,
    informes: estado?.informes ?? [],
    version,
    setVersion,
    versionActual: estado?.informes.find((i) => i.version === version) ?? null,
    html,
    urlDescarga: version != null ? urlInformeSeoTecnico(clientSlug, version, true) : null,
    ocupado,
    error,
    aviso,
    correrLighthouse,
    generar,
    ajustar,
  };
}

export type SeoTecnicoVista = ReturnType<typeof useSeoTecnico>;
